/**
 * <s-fitment-prompt> — Opens the contained <dialog> a few seconds after the
 * page loads, so buyers who are unsure about fit can call or message the shop.
 * Shows again after a cooldown instead of staying away for the whole session,
 * so the prompt appears regularly while browsing but not on every page.
 *
 * Attributes:
 *   delay       — Milliseconds to wait before opening (default 4000).
 *   cooldown    — Milliseconds before the prompt may appear again (default 180000 = 3 minutes).
 *   storage-key — sessionStorage key holding the last-shown timestamp (default "fitment-prompt").
 */
class SFitmentPrompt extends HTMLElement {
  #timer;

  connectedCallback() {
    const dialog = this.querySelector('dialog');
    if (!dialog) return;

    const key = this.getAttribute('storage-key') || 'fitment-prompt';
    const delay = Number.parseInt(this.getAttribute('delay') ?? '', 10);
    const cooldown = Number.parseInt(this.getAttribute('cooldown') ?? '', 10);
    if (this.#recentlyShown(key, Number.isFinite(cooldown) ? cooldown : 180000)) return;

    this.#timer = setTimeout(() => {
      if (dialog.open || document.querySelector('dialog[open]')) return;
      dialog.showModal();
      this.#remember(key);
    }, Number.isFinite(delay) ? delay : 4000);
  }

  disconnectedCallback() {
    clearTimeout(this.#timer);
  }

  #recentlyShown(key, cooldown) {
    try {
      const last = Number(sessionStorage.getItem(key));
      return Number.isFinite(last) && last > 0 && Date.now() - last < cooldown;
    } catch {
      return false;
    }
  }

  #remember(key) {
    try {
      sessionStorage.setItem(key, String(Date.now()));
    } catch {
      // Storage unavailable (private mode); the prompt may show again.
    }
  }
}

if (!customElements.get('s-fitment-prompt')) customElements.define('s-fitment-prompt', SFitmentPrompt);
